import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Users, Coin, ShieldCheck, FileText, CheckCircle, Bank, Warning, ArrowRight, FileMagnifyingGlass as FileSearch, Briefcase, UsersThree,
} from '@phosphor-icons/react';
import {
  getCmcHealth,
  getGovernanceSummary,
  getSocialUtilityIndicators,
  listCmcAudit,
  getOpenApiYamlUrl,
} from '../../services/cmcApi';
import { CmcPageHeader, fmtAmount, fmtDate } from '../../components/cmc/CmcCommon';
import { PageHero } from '../../components/motion/PageHero';
import { Coin as CoinIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

function MetricCard({ label, value, sublabel, icon: Icon, accent = '#2D5A4C', testid }) {
  return (
    <Card data-testid={testid} className="border-[#E8E6E1]">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[#5E6D66]">
            {label}
          </div>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}15` }}>
            <Icon size={18} weight="bold" style={{ color: accent }} />
          </div>
        </div>
        <div className="text-2xl font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
          {value}
        </div>
        {sublabel && <div className="text-xs text-[#5E6D66] mt-1">{sublabel}</div>}
      </CardContent>
    </Card>
  );
}

function QuickLink({ to, icon: Icon, label, desc, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex items-center gap-3 p-4 rounded-lg border border-[#E8E6E1] bg-white hover:border-[#2D5A4C] hover:shadow-sm transition-all"
    >
      <div className="w-10 h-10 rounded-md bg-[#F0EFEB] flex items-center justify-center group-hover:bg-[#2D5A4C]/10">
        <Icon size={20} className="text-[#2D5A4C]" weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#1A2421]">{label}</div>
        <div className="text-xs text-[#5E6D66] truncate">{desc}</div>
      </div>
      <ArrowRight size={16} className="text-[#5E6D66] group-hover:text-[#2D5A4C] transition-colors" />
    </Link>
  );
}

export default function CmcDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [governance, setGovernance] = useState(null);
  const [indicators, setIndicators] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [h, g, ind, aud] = await Promise.all([
        getCmcHealth(),
        getGovernanceSummary(),
        getSocialUtilityIndicators(),
        listCmcAudit({ limit: 8 }),
      ]);
      setHealth(h.data);
      setGovernance(g.data);
      setIndicators(ind.data);
      setAuditEvents(aud.data.items || []);
    } catch (e) {
      toast.error('Erreur lors du chargement du tableau de bord CMC');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="cmc-dash-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  const computed = indicators?.computed || {};

  return (
    <div data-testid="cmc-dashboard-page" className="space-y-6">
      <CmcPageHeader
        title="CMC O'SCOP"
        subtitle="Caisse Mutualisée des Coopérateurs — pilotage, paiements et indicateurs d'utilité sociale"
        actions={
          <>
            {health?.status === 'ok' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium" data-testid="cmc-health-ok">
                <CheckCircle size={14} weight="fill" /> API CMC opérationnelle (v{health.version})
              </span>
            )}
            <a
              href={getOpenApiYamlUrl()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cmc-openapi-link"
            >
              <Button variant="outline" size="sm" className="border-[#E8E6E1]">
                <FileSearch size={16} className="mr-2" />
                OpenAPI YAML
              </Button>
            </a>
          </>
        }
      />

      {/* Key metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <MetricCard
          testid="metric-cooperators"
          label="Coopérateurs"
          value={computed.cooperators_total ?? 0}
          icon={Users}
          accent="#2D5A4C"
        />
        <MetricCard
          testid="metric-ucc"
          label="UCC"
          value={computed.ucc_total ?? 0}
          icon={Coin}
          accent="#80C51F"
        />
        <MetricCard
          testid="metric-payments-paid"
          label="Paiements versés"
          value={fmtAmount(computed.payments_paid_total_amount)}
          icon={Bank}
          accent="#00AEEF"
        />
        <MetricCard
          testid="metric-attestations"
          label="Attestations émises"
          value={computed.attestations_issued ?? 0}
          icon={FileText}
          accent="#D5007D"
        />
        <MetricCard
          testid="metric-affectations"
          label="Affectations coop."
          value={fmtAmount(computed.affectations_cooperatives_total)}
          icon={ShieldCheck}
          accent="#FF8E00"
        />
      </div>

      {/* Governance alerts */}
      {governance && (
        <Card className="border-[#E8E6E1] mb-6">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-[#1A2421] mb-4 flex items-center gap-2">
              <ShieldCheck size={18} className="text-[#2D5A4C]" weight="bold" />
              Gouvernance & contrôles
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div data-testid="gov-pending-review">
                <div className="text-[11px] uppercase tracking-wide text-[#5E6D66]">Revues renforcées en attente</div>
                <div className="text-xl font-semibold text-[#1A2421] mt-1">{governance.pending_enhanced_reviews ?? 0}</div>
              </div>
              <div data-testid="gov-unsigned-contracts">
                <div className="text-[11px] uppercase tracking-wide text-[#5E6D66]">Contrats non signés</div>
                <div className="text-xl font-semibold text-[#1A2421] mt-1">{governance.unsigned_contracts ?? 0}</div>
              </div>
              <div data-testid="gov-revoked-attestations">
                <div className="text-[11px] uppercase tracking-wide text-[#5E6D66]">Attestations révoquées</div>
                <div className="text-xl font-semibold text-[#1A2421] mt-1">{governance.revoked_attestations ?? 0}</div>
              </div>
              <div data-testid="gov-sensitive-ucc">
                <div className="text-[11px] uppercase tracking-wide text-[#5E6D66]">UCC sensibles</div>
                <div className="text-xl font-semibold text-[#1A2421] mt-1">{governance.sensitive_ucc_items ?? 0}</div>
              </div>
            </div>
            {governance.recommended_controls?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#F0EFEB]">
                <div className="text-[11px] uppercase tracking-wide text-[#5E6D66] mb-2">
                  Contrôles recommandés
                </div>
                <ul className="space-y-1">
                  {governance.recommended_controls.map((c, i) => (
                    <li key={i} className="text-xs text-[#1A2421] flex items-start gap-2">
                      <Warning size={12} weight="fill" className="text-amber-500 mt-0.5 flex-shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick links */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <QuickLink testid="link-cooperators" to="/cmc/cooperators" icon={Users} label="Coopérateurs-contributeurs" desc="Registre des membres CMC" />
        <QuickLink testid="link-ucc" to="/cmc/ucc" icon={Coin} label="UCC" desc="Unités de contribution coopérative" />
        <QuickLink testid="link-rules" to="/cmc/rules" icon={ShieldCheck} label="Règles CMC" desc="Référentiel de qualifications et plafonds" />
        <QuickLink testid="link-contracts" to="/cmc/contracts" icon={FileText} label="Convention cadre C.I.P.A.E.S.S.E" desc="Conventions individuelles" />
        <QuickLink testid="link-cipascop" to="/cmc/cipascop" icon={FileText} label="Convention cadre C.I.P.A.S.C.O.P" desc="Convention individuelle" />
        <QuickLink testid="link-apport-industrie" to="/cmc/apport-industrie" icon={UsersThree} label="Apport en industrie" desc="Mission coopérative — individuelle & collective" />
        <QuickLink testid="link-payments" to="/cmc/payments" icon={Bank} label="Paiements coopératifs" desc="Versements & validations" />
        <QuickLink testid="link-attestations" to="/cmc/attestations" icon={ShieldCheck} label="Attestations" desc="Émission & QR de vérification" />
        <QuickLink testid="link-audit" to="/cmc/audit" icon={FileSearch} label="Audit & utilité sociale" desc="Journal et indicateurs" />
      </div>

      {/* Recent audit events */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <FileSearch size={18} className="text-[#2D5A4C]" weight="bold" />
              Derniers événements d'audit
            </h2>
            <Link to="/cmc/audit" className="text-xs text-[#2D5A4C] font-medium hover:underline" data-testid="cmc-see-all-audit">
              Voir tout →
            </Link>
          </div>
          {auditEvents.length === 0 ? (
            <div className="py-6 text-center text-sm text-[#5E6D66]">Aucun événement enregistré pour le moment.</div>
          ) : (
            <div className="divide-y divide-[#F0EFEB]">
              {auditEvents.map((ev) => (
                <div key={ev.id} className="py-2.5 flex items-start gap-3" data-testid={`audit-row-${ev.id}`}>
                  <div className="w-8 h-8 rounded-md bg-[#F0EFEB] flex items-center justify-center flex-shrink-0">
                    <FileSearch size={14} className="text-[#5E6D66]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#1A2421]">
                      <span className="font-medium capitalize">{ev.action}</span>{' '}
                      <span className="text-[#5E6D66]">{ev.entity_type}</span>
                      {ev.entity_label && <span className="text-[#1A2421]"> · {ev.entity_label}</span>}
                    </div>
                    <div className="text-[11px] text-[#5E6D66] mt-0.5">{fmtDate(ev.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
